import type { CSSProperties } from 'react';
import { formatBps } from '../../domain/equityEngine';
import type { EquitySummary, MilestoneCalculation } from '../../domain/types';

interface EquityOrbitProps {
  summary: EquitySummary;
  milestones: MilestoneCalculation[];
  selectedMilestoneId: string;
  onSelectMilestone: (id: string) => void;
}

export function EquityOrbit({
  summary,
  milestones,
  selectedMilestoneId,
  onSelectMilestone,
}: EquityOrbitProps) {
  const earnedRatio = ratio(summary.milestoneEarnedBps, summary.potentialBps);
  const vestedRatio = ratio(summary.timeVestedBps, summary.potentialBps);
  const riskRatio = ratio(
    summary.repurchaseAt80RiskBps + summary.repurchaseAtNominalRiskBps,
    summary.potentialBps,
  );
  const lapsedRatio = ratio(summary.lapsedBps + summary.blockedBps, summary.potentialBps);

  return (
    <section className="equity-orbit" aria-label="2%孵化权益装置">
      <div className="orbit-field">
        <div className="orbit-halo orbit-halo-a" />
        <div className="orbit-halo orbit-halo-b" />
        <div
          className="orbit-core"
          style={
            {
              '--earned-ratio': `${earnedRatio * 360}deg`,
              '--vested-ratio': `${vestedRatio * 360}deg`,
              '--risk-ratio': `${riskRatio * 360}deg`,
              '--lapsed-ratio': `${lapsedRatio * 360}deg`,
            } as CSSProperties
          }
        >
          <div className="orbit-core-inner">
            <span className="eyebrow">最高孵化权益</span>
            <strong>2.0%</strong>
            <span>200基点</span>
          </div>
        </div>

        {milestones.map((milestone, index) => {
          const angle = (index / milestones.length) * Math.PI * 2 - Math.PI / 2;
          const x = Math.cos(angle) * 43 + 50;
          const y = Math.sin(angle) * 43 + 50;
          return (
            <button
              className={`orbit-node status-${milestone.status} ${
                milestone.milestone.id === selectedMilestoneId ? 'is-selected' : ''
              }`}
              key={milestone.milestone.id}
              style={{ left: `${x}%`, top: `${y}%` }}
              type="button"
              onClick={() => onSelectMilestone(milestone.milestone.id)}
              aria-label={`选择${milestone.milestone.shortTitle}`}
            >
              <span>{milestone.milestone.id}</span>
            </button>
          );
        })}
      </div>

      <div className="orbit-legend" aria-label="孵化权益状态图例">
        <span>
          <i className="legend-dot earned" />
          里程碑成熟 {formatBps(summary.milestoneEarnedBps)}
        </span>
        <span>
          <i className="legend-dot vested" />
          时间归属 {formatBps(summary.timeVestedBps)}
        </span>
        <span>
          <i className="legend-dot exercisable" />
          可行权 {formatBps(summary.exercisableBps)}
        </span>
        <span>
          <i className="legend-dot risk" />
          回购风险 {formatBps(summary.repurchaseAt80RiskBps + summary.repurchaseAtNominalRiskBps)}
        </span>
      </div>
    </section>
  );
}

function ratio(value: number, total: number): number {
  if (total <= 0) {
    return 0;
  }

  return Math.max(0, Math.min(1, value / total));
}
